import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  ShieldCheck,
  FileText,
  AlertCircle,
  Plus,
  MoreHorizontal,
  Download,
  CheckCircle,
  Clock,
  Search,
  Globe2,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const tabs = [
  "Overview",
  "Timestamp Records",
  "Verification",
  "Time Sources",
  "Authorities",
  "Settings",
];

const LEGEND_ITEMS = [
  { name: "GlobalSign", color: "#6366f1" },
  { name: "Sectigo", color: "#3b82f6" },
  { name: "DigiCert", color: "#22c55e" },
  { name: "NIST", color: "#f59e0b" },
];

function ResponseTimeLegend() {
  return (
    <div className="flex gap-3 text-[10px] pb-1 justify-end">
      {LEGEND_ITEMS.map(({ name, color }) => (
        <span key={name} className="flex items-center gap-1">
          <span style={{ background: color }} className="inline-block h-2 w-2 rounded-full" />
          {name}
        </span>
      ))}
    </div>
  );
}

export default function Authorities() {
  const navigate = useNavigate();
  const [selectedAuthority, setSelectedAuthority] =
    useState("GlobalSign TSA");
  const [searchQuery, setSearchQuery] = useState("");

  const kpiCards = [
    {
      label: "Registered Authorities",
      value: "5",
      subtitle: "All Active",
      icon: <Building2 className="h-5 w-5 text-indigo-600" />,
      bg: "bg-indigo-50",
    },
    {
      label: "Trusted TSA",
      value: "5",
      subtitle: "100% Verified",
      icon: <ShieldCheck className="h-5 w-5 text-emerald-600" />,
      bg: "bg-emerald-50",
    },
    {
      label: "Certificates Issued",
      value: "12,458",
      subtitle: "18.6% vs last month",
      prefix: "↑",
      icon: <FileText className="h-5 w-5 text-blue-600" />,
      bg: "bg-blue-50",
    },
    {
      label: "Key Alerts",
      value: "0",
      subtitle: "No Critical Issues",
      icon: <AlertCircle className="h-5 w-5 text-red-600" />,
      bg: "bg-red-50",
    },
  ];

  const authoritiesData = [
    {
      name: "GlobalSign TSA",
      country: "USA",
      type: "Public TSA",
      status: "Active",
      certificate: "GTS-2025-001",
      lastSync: "May 31, 2025\n10:30 AM",
      url: "https://timestamp.globalsign.com",
      apiEndpoint: "/tsa",
      algorithms: "SHA-256, SHA-384, SHA-512",
      certificateId: "GTS-2025-001",
      validFrom: "May 1, 2025",
      validUntil: "May 31, 2026",
    },
    {
      name: "Sectigo TSA",
      country: "USA",
      type: "Public TSA",
      status: "Active",
      certificate: "SCT-2025-014",
      lastSync: "May 31, 2025\n10:28 AM",
      url: "https://timestamp.sectigo.com",
      apiEndpoint: "/tsa",
      algorithms: "SHA-256, SHA-384, SHA-512",
      certificateId: "SCT-2025-014",
      validFrom: "May 15, 2025",
      validUntil: "May 15, 2026",
    },
    {
      name: "DigiCert TSA",
      country: "USA",
      type: "Public TSA",
      status: "Active",
      certificate: "DGC-2025-022",
      lastSync: "May 31, 2025\n10:30 AM",
      url: "https://timestamp.digicert.com",
      apiEndpoint: "/tsa",
      algorithms: "SHA-256, SHA-384, SHA-512",
      certificateId: "DGC-2025-022",
      validFrom: "May 10, 2025",
      validUntil: "May 10, 2026",
    },
    {
      name: "I-PQSign TSA (Internal)",
      country: "IND",
      type: "Internal TSA",
      status: "Active",
      certificate: "INT-2025-007",
      lastSync: "May 31, 2025\n10:30 AM",
      url: "https://tsa.internal.local",
      apiEndpoint: "/tsa",
      algorithms: "SHA-256, SHA-384, SHA-512",
      certificateId: "INT-2025-007",
      validFrom: "May 1, 2025",
      validUntil: "May 1, 2026",
    },
    {
      name: "NIST Time Server",
      country: "USA",
      type: "Public NTP",
      status: "Active",
      certificate: "NIST-2025-003",
      lastSync: "May 31, 2025\n10:28 AM",
      url: "https://time.nist.gov",
      apiEndpoint: "/ntp",
      algorithms: "NTPv4",
      certificateId: "NIST-2025-003",
      validFrom: "Jan 1, 2025",
      validUntil: "Dec 31, 2025",
    },
  ];

  const timeSourcePerformanceData = [
    { date: "May 25", globalsign: 0.35, sectigo: 0.58, digicert: 0.45, nist: 0.28 },
    { date: "May 26", globalsign: 0.30, sectigo: 0.52, digicert: 0.40, nist: 0.27 },
    { date: "May 27", globalsign: 0.32, sectigo: 0.49, digicert: 0.41, nist: 0.27 },
    { date: "May 28", globalsign: 0.30, sectigo: 0.58, digicert: 0.46, nist: 0.31 },
    { date: "May 29", globalsign: 0.30, sectigo: 0.50, digicert: 0.39, nist: 0.29 },
    { date: "May 30", globalsign: 0.36, sectigo: 0.50, digicert: 0.52, nist: 0.40 },
    { date: "May 31", globalsign: 0.41, sectigo: 0.60, digicert: 0.55, nist: 0.41 },
  ];

  const certificateOverviewData = [
    { name: "GlobalSign TSA", value: 2450, max: 3000, color: "#6366f1" },
    { name: "Sectigo TSA", value: 2200, max: 3000, color: "#3b82f6" },
    { name: "DigiCert TSA", value: 2100, max: 3000, color: "#22c55e" },
    { name: "I-PQSign TSA", value: 1890, max: 2000, color: "#6366f1" },
    { name: "NIST Time Server", value: 670, max: 1000, color: "#94a3b8" },
  ];

  const auditLogs = [
    {
      event: "Certificate issued by GlobalSign TSA",
      id: "TXN-7845",
      time: "10:30 AM",
    },
    {
      event: "Authority sync completed",
      authority: "Sectigo TSA",
      time: "10:29 AM",
    },
    {
      event: "Certificate verified successfully",
      document: "SER-Agreement.pdf",
      time: "10:28 AM",
    },
    {
      event: "System configuration updated",
      by: "Super Admin",
      time: "10:24 AM",
    },
  ];

  const selectedAuthDetails =
    authoritiesData.find((auth) => auth.name === selectedAuthority) ||
    authoritiesData[0];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800 -mx-6 -mt-6">
      {/* Tab Header */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-2">
          <nav className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === "Authorities") return;
                  if (tab === "Overview") navigate("/timestamps");
                  else if (tab === "Timestamp Records")
                    navigate("/timestamps/records");
                  else if (tab === "Verification")
                    navigate("/timestamps/verification");
                  else if (tab === "Time Sources")
                    navigate("/timestamps/TimeSource");
                  else if (tab === "Settings")
                    navigate("/timestamps/settings");
                  else navigate("/timestamps");
                }}
                className={`pb-2 text-xs font-medium transition-colors ${
                  tab === "Authorities"
                    ? "border-b-2 border-indigo-600 text-indigo-600"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
          <button className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition">
            + Add Authority
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1500px] space-y-4 px-6 pt-4">
        {/* Compact KPI Cards */}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {kpiCards.map((card) => (
            <div
              key={card.label}
              className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className={`rounded-lg p-2.5 ${card.bg}`}>
                  {card.icon}
                </div>
                <div>
                  <p className="text-[11px] font-medium text-slate-500">{card.label}</p>
                  <p className="text-xl font-bold text-slate-900">{card.value}</p>
                  <p className="mt-0.5 text-[11px] font-medium text-emerald-600">
                    {card.prefix && <span>{card.prefix} </span>}
                    {card.subtitle}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Main Content */}
        <section className="space-y-3">
          {/* Top Row: Table + Authority Details */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
            {/* Authorities Table */}
            <div className="rounded-lg border border-slate-100 bg-white p-3 shadow-xs lg:col-span-8">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-bold text-slate-900">
                  Trusted Timestamp Authorities
                </h2>

                <button className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-700">
                  <Plus className="h-3.5 w-3.5" />
                  Add Authority
                </button>
              </div>

              {/* Search and Filters */}
              <div className="mb-3 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    placeholder="Search by name, country, or status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 py-1.5 pl-7 pr-3 text-xs focus:border-indigo-600 focus:outline-none"
                  />
                </div>

                <select className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs focus:outline-none">
                  <option>Status: All</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>

                <select className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs focus:outline-none">
                  <option>Country: All</option>
                  <option>USA</option>
                  <option>IND</option>
                </select>

                <select className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs focus:outline-none">
                  <option>Authority Type: All</option>
                  <option>Public TSA</option>
                  <option>Internal TSA</option>
                  <option>Public NTP</option>
                </select>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-2 py-2 text-[10px] font-semibold text-slate-600">
                        Authority Name
                      </th>
                      <th className="px-2 py-2 text-[10px] font-semibold text-slate-600">
                        Country
                      </th>
                      <th className="px-2 py-2 text-[10px] font-semibold text-slate-600">
                        Type
                      </th>
                      <th className="px-2 py-2 text-[10px] font-semibold text-slate-600">
                        Status
                      </th>
                      <th className="px-2 py-2 text-[10px] font-semibold text-slate-600">
                        Certificate
                      </th>
                      <th className="px-2 py-2 text-[10px] font-semibold text-slate-600">
                        Last Sync
                      </th>
                      <th className="px-2 py-2 text-[10px] font-semibold text-slate-600">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-50">
                    {authoritiesData.map((auth) => (
                      <tr
                        key={auth.name}
                        className="cursor-pointer hover:bg-slate-50/50"
                        onClick={() => setSelectedAuthority(auth.name)}
                      >
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                            <span className="font-semibold text-slate-800">
                              {auth.name}
                            </span>
                          </div>
                        </td>

                        <td className="px-2 py-2.5 text-slate-600">
                          <span className="text-lg">
                            {auth.country === "USA" ? "🇺🇸" : "🇮🇳"}
                          </span>{" "}
                          {auth.country}
                        </td>

                        <td className="px-2 py-2.5 text-slate-600">
                          {auth.type}
                        </td>

                        <td className="px-2 py-2.5">
                          <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                            {auth.status}
                          </span>
                        </td>

                        <td className="px-2 py-2.5 font-semibold text-blue-600">
                          {auth.certificate}
                        </td>

                        <td className="whitespace-pre-line px-2 py-2.5 text-slate-600">
                          {auth.lastSync}
                        </td>

                        <td className="px-2 py-2.5">
                          <button className="text-slate-400 hover:text-slate-600">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Authority Details */}
            <div className="lg:col-span-4">
            <div className="rounded-lg border border-slate-100 bg-white p-3 shadow-xs">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">
                  Authority Details
                </h3>
              </div>

              <div className="mb-2 flex items-center gap-2">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50">
                    <Globe2 className="h-6 w-6 text-blue-600" />
                  </div>

                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">
                    {selectedAuthDetails.name}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    Global leader providing secure authenticated time-stamping
                    services worldwide.
                  </p>
                </div>

                <span className="rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-600">
                  Active
                </span>
              </div>

              <div className="space-y-1.5 text-[11px]">
                <div className="flex gap-2 border-b border-slate-50 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">Country</span>
                  <span className="font-medium text-slate-800">
                    {selectedAuthDetails.country === "USA" ? "🇺🇸" : "🇮🇳"}{" "}
                    {selectedAuthDetails.country === "USA" ? "United States" : "India"}
                  </span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">Type</span>
                  <span className="font-medium text-slate-800">{selectedAuthDetails.type}</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">URL</span>
                  <a href={selectedAuthDetails.url} target="_blank" rel="noreferrer" className="break-all font-medium text-blue-600 hover:underline">
                    {selectedAuthDetails.url.replace("https://", "")}
                  </a>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">API Endpoint</span>
                  <span className="font-mono text-[10px] text-slate-800">{selectedAuthDetails.apiEndpoint}</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">Algorithms</span>
                  <span className="font-medium text-slate-800">{selectedAuthDetails.algorithms}</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">Certificate ID</span>
                  <span className="font-medium text-slate-800">{selectedAuthDetails.certificateId}</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">Valid From</span>
                  <span className="font-medium text-slate-800">{selectedAuthDetails.validFrom}</span>
                </div>
                <div className="flex gap-2 pb-1">
                  <span className="w-28 shrink-0 text-slate-500">Valid Until</span>
                  <span className="font-medium text-slate-800">{selectedAuthDetails.validUntil}</span>
                </div>
              </div>

              <div className="mt-3 flex gap-2 border-t border-slate-50 pt-2">
                <button className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-indigo-600 px-2 py-1.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50">
                  <Download className="h-3.5 w-3.5" />
                  Download Certificate
                </button>

                <button className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Test Connection
                </button>
              </div>
            </div>
            </div>
          </div>

          {/* Bottom 3 Cards */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {/* Authority Performance Chart */}
            <div className="rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">Authority Performance</h3>
              </div>
              <div className="h-[180px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeSourcePerformanceData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#f1f5f9" }} />
                    <YAxis width={90} domain={[0.0, 1.0]} ticks={[0.0, 0.2, 0.4, 0.6, 0.8, 1.0]} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false}
                      label={{ value: "Response Time (seconds)", angle: -90, position: "insideLeft", offset: 20, style: { fontSize: 11, fill: "#475569", textAnchor: "middle", fontWeight: 500 } }}
                    />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #f1f5f9", fontSize: "11px" }} />
                    <Legend verticalAlign="top" align="right" content={<ResponseTimeLegend />} />
                    <Line type="monotone" dataKey="globalsign" name="GlobalSign" stroke="#6366f1" strokeWidth={2} dot={{ r: 2.5 }} />
                    <Line type="monotone" dataKey="sectigo" name="Sectigo" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2.5 }} />
                    <Line type="monotone" dataKey="digicert" name="DigiCert" stroke="#22c55e" strokeWidth={2} dot={{ r: 2.5 }} />
                    <Line type="monotone" dataKey="nist" name="NIST" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2.5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* Certificate Overview */}
            <div className="rounded-lg border border-slate-100 bg-white p-3 shadow-xs">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">
                  Certificate Overview
                </h3>
                <a
                  href="#"
                  className="text-xs font-semibold text-indigo-600 hover:underline"
                >
                  View All
                </a>
              </div>

              <div className="space-y-1.5">
                {certificateOverviewData.map((cert) => (
                  <div key={cert.name} className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium text-slate-700">
                        {cert.name}
                      </p>

                      <div className="relative mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(cert.value / cert.max) * 100}%`,
                            backgroundColor: cert.color,
                          }}
                        />
                      </div>
                    </div>

                    <span className="whitespace-nowrap text-[10px] font-semibold text-slate-600">
                      {cert.value}/{cert.max}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Audit Log */}
            <div className="rounded-lg border border-slate-100 bg-white p-3 shadow-xs">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">Recent Audit Log</h3>
                <a
                  href="#"
                  className="text-xs font-semibold text-indigo-600 hover:underline"
                >
                  View All
                </a>
              </div>

              <div className="space-y-1">
                {auditLogs.map((log, idx) => (
                  <div
                    key={`${log.event}-${log.time}`}
                    className="flex gap-2 border-b border-slate-50 pb-1 last:border-0 last:pb-0"
                  >
                    <div className="mt-0.5">
                      {idx === 0 && (
                        <FileText className="h-3.5 w-3.5 text-indigo-600" />
                      )}
                      {idx === 1 && (
                        <CheckCircle className="h-3.5 w-3.5 text-blue-600" />
                      )}
                      {idx === 2 && (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                      )}
                      {idx === 3 && (
                        <Clock className="h-3.5 w-3.5 text-slate-600" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium text-slate-700">
                        {log.event}
                      </p>

                      {log.id && (
                        <p className="text-[10px] text-slate-500">
                          Transaction ID: {log.id}
                        </p>
                      )}

                      {log.authority && (
                        <p className="text-[10px] text-slate-500">
                          {log.authority}
                        </p>
                      )}

                      {log.document && (
                        <p className="text-[10px] text-slate-500">
                          {log.document}
                        </p>
                      )}

                      {log.by && (
                        <p className="text-[10px] text-slate-500">
                          By {log.by}
                        </p>
                      )}
                    </div>

                    <span className="whitespace-nowrap text-[10px] font-medium text-slate-500">
                      {log.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}